import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';

import { store } from 'app/store';
import { setLocale } from 'features/intl/intlSlice';
import { fetchUser, IUser, loginUser } from 'features/user/userAPI';
import { DEFAULT_LOCALE } from 'helpers/consts';

// Authentication states
export type AuthStatus =
  | 'checking' // Initial load - checking for existing session
  | 'anonymous' // No valid session, user not logged in
  | 'authenticated' // Valid session, user data loaded
  | 'loading' // Transitioning (logging in, logging out, refreshing)
  | 'error'; // Something went wrong

export interface AuthState {
  status: AuthStatus;
  user: IUser | null;
  error: string | null;
}

const initialState: AuthState = {
  status: 'checking',
  user: null,
  error: null,
};

// Helper to decode JWT and extract user ID
export function getUserIdFromToken(): number | null {
  const token = Cookies.get('token');
  if (!token) return null;
  try {
    const decoded: any = jwtDecode(token);
    return decoded?.data?.user?.id || null;
  } catch {
    return null;
  }
}

export function getMyId(): number | null {
  return store.getState().auth.user?.id || null;
}

// Initialize auth - check for existing session
export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { rejectWithValue }) => {
    try {
      const userId = getUserIdFromToken();
      if (!userId) {
        // No valid token
        return { user: null, status: 'anonymous' as const };
      }
      // Valid token, fetch user data
      const response = await fetchUser(userId);
      return { user: response.data, status: 'authenticated' as const };
    } catch (error: any) {
      // Token exists but invalid, clear it
      Cookies.remove('token');
      return rejectWithValue({ response: error });
    }
  },
);

// Login action
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { email: string; password: string }, thunkAPI) => {
    try {
      // // Import loginUser here to avoid circular deps
      // const { loginUser } = await import('features/user/userAPI');
      const loginResponse = await loginUser(credentials);
      // Get user ID from the newly set token
      const userId = getUserIdFromToken();
      if (!userId) throw new Error('Login succeeded but no valid token');
      // Fetch complete user data
      const userResponse = await fetchUser(userId);
      thunkAPI.dispatch(setLocale(userResponse.data.locale || DEFAULT_LOCALE));
      return { response: userResponse };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

// Logout action
export const logout = createAsyncThunk('auth/logout', async (_, thunkAPI) => {
  try {
    // Flush any pending UI state changes using sync version (sendBeacon)
    // Uses sync to avoid race condition where user state is cleared before async completes
    Cookies.remove('token');
    return { response: null };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({ response: error });
  }
});

// Refresh user data (after profile updates, etc.)
export const refreshUser = createAsyncThunk(
  'auth/refresh',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      const userId = state.auth.user?.id;
      if (!userId) {
        throw new Error('No user to refresh');
      }
      const response = await fetchUser(userId);
      return { response: response };
    } catch (error: any) {
      return rejectWithValue({ response: error });
    }
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Initialize
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.status = action.payload.status;
        state.user = action.payload.user;
        state.error = null;
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        state.status = 'anonymous';
        state.user = null;
        state.error = null;
      })

      // Login
      .addCase(login.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = 'authenticated';
        state.user = action.payload.response.data;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'anonymous';
        state.user = null;
        state.error = action.payload as string;
      })

      // Logout
      .addCase(logout.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(logout.fulfilled, (state) => {
        state.status = 'anonymous';
        state.user = null;
        state.error = null;
      })

      // Refresh
      .addCase(refreshUser.fulfilled, (state, action) => {
        if (state.status === 'authenticated') {
          state.user = action.payload.response.data;
        }
      })
      .addCase(refreshUser.rejected, (state) => {
        // If refresh fails, logout
        state.status = 'anonymous';
        state.user = null;
        state.error = null;
      });
  },
});

export const { clearError } = authSlice.actions;
export default authSlice.reducer;

// Selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth;
export const selectAuthStatus = (state: { auth: AuthState }) =>
  state.auth.status;
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectMyId = (state: { auth: AuthState }) => state.auth.user?.id;
export const selectIsAuthenticated = (state: { auth: AuthState }) =>
  state.auth.status === 'authenticated';
export const selectIsLoading = (state: { auth: AuthState }) =>
  state.auth.status === 'loading' || state.auth.status === 'checking';
export const selectIsReady = (state: { auth: AuthState }) =>
  state.auth.status !== 'checking';
